// Tiered wrong-answer feedback for the exercise: instead of one fixed harsh "Not quite correct",
// the phrase, its color and the face's expression follow HOW CLOSE the typed attempt is to the
// correct answer — restricted Damerau-Levenshtein (answer_diff.js) normalized by length, bucketed
// into 5 tiers from "tough one, take your time" (deep red, sad face) to "Almost there!" (amber,
// excited face). Phrases deliberately never say WHAT is wrong (no "one letter off" hints) —
// encouragement only; the tone/color ladder itself is the only hot-cold signal.
// Used by training_session.js on Enter-with-wrong-answer while the timer is still running
// (a timeout reveals the answer instead and shows no phrase). Pure string->html, no DOM access.
var WrongFeedback = (function () {
	"use strict";

	// Phrase resource keys per tier (resources_*.js); rotated randomly within the tier,
	// never repeating the previously shown phrase.
	var TIER_KEYS = [
		["tsWrongT1_1", "tsWrongT1_2", "tsWrongT1_3"],
		["tsWrongT2_1", "tsWrongT2_2", "tsWrongT2_3"],
		["tsWrongT3_1", "tsWrongT3_2", "tsWrongT3_3"],
		["tsWrongT4_1", "tsWrongT4_2", "tsWrongT4_3"],
		["tsWrongT5_1", "tsWrongT5_2", "tsWrongT5_3"]
	];

	var lastKey = null;

	// similarity = 1 - dist/maxLen against the NEAREST accepted form, so "die Kunde" is scored
	// against "der Kunde", not against some longer synonym form it wasn't aiming for.
	// Both numbers are exposed: the tier drives the feedback ladder, and training_session.js
	// reports {tier, similarity} with each wrong answer's user_answer_event row.
	function score(forms, attempt, caseSensitive) {
		var best = AnswerDiff.closest(attempt, forms, caseSensitive);
		var dist = AnswerDiff.distance(best, attempt, caseSensitive);
		var similarity = 1 - dist / Math.max(best.length, attempt.length, 1);
		var t;
		if (similarity >= 0.85) t = 5;
		else if (similarity >= 0.65) t = 4;
		else if (similarity >= 0.45) t = 3;
		else if (similarity >= 0.25) t = 2;
		else t = 1;
		return {tier: t, similarity: similarity};
	}

	function tier(forms, attempt, caseSensitive) {
		return score(forms, attempt, caseSensitive).tier;
	}

	function pickPhrase(t) {
		var keys = TIER_KEYS[t - 1];
		var idx = Math.floor(Math.random() * keys.length);
		if (keys[idx] === lastKey) {
			idx = (idx + 1) % keys.length;
		}
		lastKey = keys[idx];
		return getRes(lastKey);
	}

	// One vector face, five expressions — brows, eye size and mouth are chosen by tier so the
	// face visibly perks up as attempts get closer (sad -> neutral -> attentive -> excited; the
	// closest tier gets an open excited mouth, NOT a smile — smiles stay reserved for correct).
	// Everything draws with currentColor, so the CSS tier class colors label and face together.
	var FACES = [
		//t1: sympathetic — inner brow ends raised, deep frown
		{eyeR: 1.4, eyeY: 9.8, brows: ["M6.9 7.6 9.7 6.7", "M14.3 6.7 17.1 7.6"], mouth: "M8 17 Q12 13.6 16 17"},
		//t2: shallow frown
		{eyeR: 1.4, eyeY: 9.8, brows: [], mouth: "M8.3 16.6 Q12 14.7 15.7 16.6"},
		//t3: nearly flat mouth
		{eyeR: 1.4, eyeY: 9.8, brows: [], mouth: "M8.6 16.1 Q12 15.4 15.4 16.1"},
		//t4: attentive "oh" — raised brows, small open mouth
		{eyeR: 1.5, eyeY: 9.8, brows: ["M7 7 Q8.5 6 10 7", "M14 7 Q15.5 6 17 7"], open: [1.5, 1.9, 16.2]},
		//t5: excited — wide eyes, brows high, big open mouth
		{eyeR: 1.7, eyeY: 9.6, brows: ["M7 6.6 Q8.5 5.5 10 6.6", "M14 6.6 Q15.5 5.5 17 6.6"], open: [2.3, 2.7, 16.4]}
	];

	function stroke(d, w) {
		return "<path d='" + d + "' fill='none' stroke='currentColor' stroke-width='" + w + "' stroke-linecap='round'/>";
	}

	function eye(cx, f) {
		return "<circle cx='" + cx + "' cy='" + f.eyeY + "' r='" + f.eyeR + "' fill='currentColor'/>";
	}

	// Shared by faceSvg (Practice's 5-tier ladder) and moodSvg (Learning's plain good/bad face):
	// same circle + eyes + brows + mouth construction, just a different face config and wrapper.
	function drawFace(f, cls, id) {
		var h = "<svg" + (id ? " id='" + id + "'" : "") + " class='" + cls + "' viewBox='0 0 24 24' aria-hidden='true'>";
		h += "<circle cx='12' cy='12' r='10' fill='none' stroke='currentColor' stroke-width='1.8'/>";
		h += eye(8.5, f) + eye(15.5, f);
		for (var i = 0; i < f.brows.length; i++) {
			h += stroke(f.brows[i], 1.4);
		}
		h += f.open
			? "<ellipse cx='12' cy='" + f.open[2] + "' rx='" + f.open[0] + "' ry='" + f.open[1] + "' fill='none' stroke='currentColor' stroke-width='1.8'/>"
			: stroke(f.mouth, 1.8);
		return h + "</svg>";
	}

	function faceSvg(t) {
		return drawFace(FACES[t - 1], "tsWrongT" + t, "wrongFaceId");
	}

	// forms = acceptedForms(term), attempt = normalized non-empty user input.
	function render(forms, attempt, caseSensitive) {
		var t = tier(forms, attempt, caseSensitive);
		return "<span id='resultLabel' class='tsWrongT" + t + "'>" + pickPhrase(t) + "</span>" + faceSvg(t);
	}

	// The plain two-state face Learning uses instead of a ":)"/":(" — same line art as the
	// Practice ladder (bad reuses tier 1's sympathetic frown) so the app's face reads as one
	// visual language, just without the 5-tier color/expression ramp Practice needs.
	var MOOD_FACES = {
		bad: FACES[0],
		good: {eyeR: 1.4, eyeY: 9.8, brows: [], mouth: "M8.3 15 Q12 19 15.7 15"}
	};

	function moodSvg(mood, cls) {
		return drawFace(MOOD_FACES[mood], cls, null);
	}

	return {render: render, tier: tier, score: score, moodSvg: moodSvg};
})();
